/**
 * The controls that say a 词典 headword out loud.
 *
 * A pilot of the entries carries two recordings per reading, a male voice and a
 * female one, made ahead of time and served as ordinary site files. See
 * `docs/audio.md` and `partials/cidian-audio.html`.
 *
 * **The controls are hidden in the markup and shown here**, the way the 例句
 * copy buttons are. Playback is JavaScript's, so a visitor without it is shown
 * nothing rather than a control that does nothing when it is pressed.
 *
 * **One `Audio` for the page rather than one per control.** Pressing the other
 * voice, or another reading, while the first is still going should replace it
 * rather than talk over it, and one element gives that for free. It also means
 * one thing to stop when a reader presses the same control twice. A 多音字 has
 * a pair of controls under each of its readings, so there can be eight on a
 * page.
 *
 * **Nothing is preloaded.** A file is about 10 KB, and a page carrying eight of
 * them is 80 KB nobody asked for on every 词典 page a reader passes through
 * without pressing anything. The `src` is set at the moment of the press.
 */

/** The attribute a control names its recording in. */
const fileAttribute = "data-cb-audio";

/** The attribute set on whichever control is sounding. */
const playingAttribute = "data-cb-playing";

/**
 * Wire up the audio controls on a 词典 entry.
 *
 * Does nothing on a page with no controls, which is every page the pilot has
 * not reached. A page in it may still carry controls under one reading and none
 * under another, where the second reading had no character to borrow from.
 * @param page - The document to look in.
 */
export function startCidianAudio(page: Document): void {
  const controls = [...page.querySelectorAll(`button[${fileAttribute}]`)];

  if (controls.length === 0) {
    return;
  }

  const player = new Audio();
  let sounding: Element | undefined;

  /** Take the mark off whichever control has it. */
  const quiet = (): void => {
    sounding?.removeAttribute(playingAttribute);
    sounding = undefined;
  };

  player.addEventListener("ended", quiet);

  /*
   * A file that will not load leaves the control unmarked rather than stuck
   * looking busy. The page names its recordings and the bucket holds them, so
   * this is the two disagreeing, which is worth failing quietly on the page and
   * loudly in the generator.
   */
  player.addEventListener("error", quiet);

  for (const control of controls) {
    control.removeAttribute("hidden");

    /*
     * Read once at wiring rather than on each press. The selector above is
     * what guarantees it is there, which is also why nothing here checks: a
     * control without the attribute was never in this list.
     */
    // oxlint-disable-next-line typescript/no-non-null-assertion -- the selector is the guarantee
    const file = control.getAttribute(fileAttribute)!;

    control.addEventListener("click", () => {
      /*
       * Pressing the control that is already sounding stops it. A reader who
       * has heard enough should not have to wait out the rest of the word, and
       * a second press is what they will try.
       */
      const wasSounding = sounding === control;

      player.pause();
      quiet();

      if (wasSounding) {
        return;
      }

      player.src = file;
      sounding = control;
      control.setAttribute(playingAttribute, "");

      /*
       * A rejected play leaves nothing marked. Browsers refuse one that no
       * gesture asked for, which cannot happen here, and refuse a file that
       * will not decode, which can.
       */
      player.play().catch(quiet);
    });
  }
}
